import Alpine from "alpinejs";
import { storefront } from "@app/lib/storefront";
import { session } from "@app/lib/session";
import { GET_VARIANT_STORE_AVAILABILITY } from "@app/gql/queries";
import { cloud } from "@app/lib/cloud"
import { parse } from "uuid";

type ShopifyAddress = {
  address1: string,
  address2: string
  city: string,
  country: string,
  countryCode: string,
  formatted: string
  latitude: number
  longitude: number
  phone: string,
  province: string
  provinceCode: string
  zip: string
}

type ShopifyStoreLocation = {
  id: string,
  location: ShopifyAddress,
  name: string
}

type ShopifyStoreAvailability = {
  available?: boolean
  location?: ShopifyStoreLocation
  pickUpTime?: string
  quantityAvailable?: number
}

type Position = {
  latitude?: number,
  longitude?: number,
}

type VariantLocations = {
  [id: string]: ShopifyStoreAvailability[]
}


const storeFinder = {
  init() {
    const useTestData = false
    this.latLng = this.getLatLng(useTestData)
  },
  latLng: {} as Position,
  product: '' as string,
  locations: [] as ShopifyStoreAvailability[],
  variants: {} as VariantLocations,
  variant: '' as string,
  error: '' as string,
  isLoading: false,

  toggleIsLoading() {
    this.isLoading = !this.isLoading
  },

  clearLocations() {
    this.locations = []
  },

  setVariant(variant: string): void {
    this.variant = variant
    if (!Object.keys(this.variants).length) return
    this.locations = this.variants[this.variant]
  },

  setProduct(id: string) {
    this.product = `gid://shopify/Product/${id}`
  },

  getLatLng(useTest: boolean): Position {
    if (useTest) {
      // coords for some random location in New Zealand 
      return {
        latitude: -37.84894022147084,
        longitude: 175.3907200264839
      }
    }
    const currentLatLng = session.geoCoords.value
    if (!currentLatLng) return {} as Position
    const lngLng = JSON.parse(currentLatLng);
    return lngLng
  },
  parseVariantObject(variants: { node: { id: string, storeAvailability: { edges: { node: ShopifyStoreAvailability }[] } } }[]) {
    return variants.reduce((acc: { [key: string]: ShopifyStoreAvailability[] }, { node }) => {
      const { id, storeAvailability } = node
      const splitId = id.split('/').pop() as string
      acc[splitId] = storeAvailability.edges.map(({ node }: any) => node)
      return acc;
    }, {})

  },

  validateAddress(locality: string) {
    const VALIDATE_SUBURB = '/location/geo-address';
    type AddressGeocodeResponse = {
      "status": "SUCCESS",
      "messages": [],
      payload: {
        attributes: {
          Latitude: string
          Longitude: string
        },
        country: string,
        fullAddress: string,

      }[]
    }

    cloud.post<AddressGeocodeResponse, { country: string, fullAddress: string, }>(VALIDATE_SUBURB, { country: "New Zealand", fullAddress: locality })
      .then(response => {
        if (!response || !response?.payload) {
          this.clearLocations()
          return;
        }
        const data = response.payload
        const match = data.length > 1 ? data.find(location => location.fullAddress === locality) : data.pop()
        const Latitude = match?.attributes?.Latitude;
        const Longitude = match?.attributes?.Longitude;
        if (!Latitude || !Longitude) {
          this.clearLocations()
          return
        }
        const latitude = parseFloat(Latitude)
        const longitude = parseFloat(Longitude)
        const latLng = {
          latitude,
          longitude
        }
        session.geoCoords.value = JSON.stringify(latLng)
        this.latLng = latLng
        this.getStores(this.product)
      })

  },

  getStores(id: string) {
    this.toggleIsLoading()
    storefront.query({
      query: GET_VARIANT_STORE_AVAILABILITY,
      variables: {
        id,
        latitude: this?.latLng?.latitude,
        longitude: this?.latLng?.longitude
      },
      fetchPolicy: 'no-cache'
    })
      .then(response => {
        if (!response?.data?.product?.variants?.edges) return
        this.variants = this.parseVariantObject(response?.data?.product?.variants?.edges ?? [])
        const storeAvailability = this.variants[this.variant]
        this.locations = storeAvailability
        this.toggleIsLoading()
      })
      .catch(error => {
        const err = error as Error
        this.error = err.message
        this.toggleIsLoading()
      })
  },

  getShopifyStoreAvailability() {
    this.error = ''
    try {
      if (!this.variant) throw 'Please select a variant.'
      const id = this.product
      navigator.geolocation.getCurrentPosition((position) => {
        const latLng = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        }
        session.geoCoords.value = JSON.stringify(latLng)
        this.latLng = latLng
        this.getStores(id)
      })
    }
    catch (error) {
      this.error = error as string
    }
  },


  hasAvailableStockInStore(store: ShopifyStoreAvailability) {
    if (!store?.quantityAvailable) return false
    return store?.quantityAvailable > 0
  },

  clickAndCollect(store: ShopifyStoreAvailability) {
    if (store?.pickUpTime) {
      return store.pickUpTime
    }
    const clickAndCollectCutOff = 16
    const now = new Date();
    return now.getHours() >= clickAndCollectCutOff;
  }
}

Alpine.store("store_finder", storeFinder);